// The frame every screen of both products sits in (docs/DESIGN.md, "The shell"): a skip link, the
// top bar, the sidebar — a drawer at 900px and below — and the main region.

import { useEffect, useState, type ReactNode } from "react";
import type { Activity, District } from "../activities";
import type { Product, Section, TabSpec } from "../products";
import type { CoursePlan, Need } from "../home";
import { PlanNav } from "./PlanNav";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

interface Props {
  product: Product;
  products: Product[];
  isAdmin: boolean;
  district: District | null;
  tabs: TabSpec[];
  section: Section;
  onProduct: (p: Product) => void;
  onSection: (s: Section) => void;
  activities: Activity[] | null;
  activitiesAvailable: boolean;
  activity: Activity | null;
  onActivity: (a: Activity) => void;
  // The class gate refused the whole product (403, or a 409 on a read): the sidebar is emptied,
  // because every link in it would lead to the same refusal.
  bare?: boolean;
  // The tab to mark as current, when it differs from where the router put you — an admin tab
  // opened without admin tools stays the one highlighted, beside the page that explains it.
  currentSection?: Section;
  activitiesFailed?: boolean;
  // Student Learning only: the curriculum plan, which IS its sidebar. `null` until it answers.
  courses?: CoursePlan[] | null;
  /** The cross-cohort queue, so the tree can say which folder is holding work. */
  needs?: Need[];
  /** The lesson open now, by the href its node carries. */
  activeHref?: string | null;
  onOpenLesson?: (href: string) => void;
  /** The plan changed from inside the nav — a unit or a lesson added — so re-read it. */
  onPlanChanged?: () => void;
  /** Shown above the main region — what needs a person, across every cohort. */
  strip?: ReactNode;
  /** School years on offer, and the one being shown. The selector lives in the account menu. */
  years?: number[];
  year?: number | null;
  onYear?: (y: number) => void;
  children: ReactNode;
}

export function Shell(p: Props) {
  const [navOpen, setNavOpen] = useState(false);
  // Navigating closes the drawer, as does Esc (docs/DESIGN.md, "The shell").
  useEffect(() => { setNavOpen(false); }, [p.product, p.section, p.activity?.activity_id]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className={"shell" + (navOpen ? " nav-open" : "")}>
      <a className="skip" href="#main">Skip to content</a>
      <TopBar product={p.product} products={p.products} isAdmin={p.isAdmin} district={p.district}
              navOpen={navOpen}
              onToggleNav={() => setNavOpen(!navOpen)} onProduct={p.onProduct}
              years={p.years} year={p.year} onYear={p.onYear}
              // Writing only: School Improvement reaches its screens through its own tab strip,
              // and a menu item duplicating a visible tab is a second way to the same place.
              onGlossary={p.product === "writing" ? () => p.onSection("glossary") : undefined} />
      {/* Student Learning's nav is its curriculum plan; School Improvement's is its tabs. The
          two products navigate differently because they ARE different: one is a year of lessons,
          the other a set of workbenches. */}
      {p.product === "writing" && !p.bare ? (
        <div className="sidebar" id="sidebar">
          {p.courses === null || p.courses === undefined
            ? <p className="pn-empty">Reading your plan…</p>
            : <PlanNav courses={p.courses} activeHref={p.activeHref ?? null}
                       needs={p.needs ?? []}
                       onChanged={p.onPlanChanged ?? (() => {})}
                       onOpen={p.onOpenLesson ?? (() => {})} />}
        </div>
      ) : (
        <Sidebar product={p.product} tabs={p.bare ? [] : p.tabs} section={p.currentSection ?? p.section}
                 onSection={p.onSection} bare={p.bare}
                 activities={p.activities} activitiesAvailable={p.activitiesAvailable}
                 activitiesFailed={p.activitiesFailed}
                 activity={p.activity} onActivity={p.onActivity} />
      )}
      <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      {/* `.main` is a flex ROW, which is what every screen below expects. The strip has to stack
          ABOVE that row rather than become a column beside it, so it gets its own wrapper. */}
      <main className="main" id="main" tabIndex={-1}>
        {p.strip ? (
          <div className="mainstack">
            {p.strip}
            <div className="mainbody">{p.children}</div>
          </div>
        ) : p.children}
      </main>
    </div>
  );
}
